import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MusicCommandAction, MusicPlayerState, MusicRepeatMode, MusicTrack, musicService } from '../../api/music';

type LastRemote = { action: MusicCommandAction; by: string | null; at: string } | null;

type MusicPlayerContextValue = {
    queue: MusicTrack[];
    index: number;
    current: MusicTrack | null;
    playlistId: string | null;
    isPlaying: boolean;
    position: number;
    duration: number;
    volume: number;
    shuffle: boolean;
    repeat: MusicRepeatMode;
    lastRemote: LastRemote;
    playQueue: (tracks: MusicTrack[], startIndex?: number, playlistId?: string | null) => void;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    next: () => void;
    prev: () => void;
    seek: (seconds: number) => void;
    setVolume: (volume: number) => void;
    setShuffle: (value: boolean) => void;
    setRepeat: (value: MusicRepeatMode) => void;
    /** Poll the API for remote commands while a page needs it (e.g. /music is open). */
    setRemoteListening: (value: boolean) => void;
    /** Drop removed/updated tracks from the queue. */
    syncTracks: (tracks: MusicTrack[]) => void;
};

const MusicPlayerContext = createContext<MusicPlayerContextValue | null>(null);

const REMOTE_POLL_MS = 5000;
const REPORT_POSITION_MS = 15000;
const VOLUME_STORAGE_KEY = 'musicPlayerVolume';

const isUrlFresh = (track: MusicTrack): boolean => {
    const expires = Date.parse(track.stream_url_expires_at || '');
    return Number.isFinite(expires) && expires - Date.now() > 60_000;
};

export const MusicPlayerProvider = ({ children }: { children: React.ReactNode }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    if (audioRef.current === null && typeof Audio !== 'undefined') {
        audioRef.current = new Audio();
        audioRef.current.preload = 'metadata';
    }

    const [queue, setQueue] = useState<MusicTrack[]>([]);
    const [index, setIndex] = useState(-1);
    const [playlistId, setPlaylistId] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolumeState] = useState<number>(() => {
        const stored = Number(localStorage.getItem(VOLUME_STORAGE_KEY));
        return Number.isFinite(stored) && stored > 0 && stored <= 1 ? stored : 0.8;
    });
    const [shuffle, setShuffle] = useState(false);
    const [repeat, setRepeat] = useState<MusicRepeatMode>('off');
    const [remoteListening, setRemoteListening] = useState(false);
    const [lastRemote, setLastRemote] = useState<LastRemote>(null);

    const current = index >= 0 && index < queue.length ? queue[index] : null;

    // Latest values for audio event handlers / async callbacks.
    const stateRef = useRef({ queue, index, shuffle, repeat, position, current });
    stateRef.current = { queue, index, shuffle, repeat, position, current };

    const loadAndPlay = useCallback(async (track: MusicTrack, autoplay = true, startAt = 0) => {
        const audio = audioRef.current;
        if (!audio) return;
        let url = track.stream_url;
        if (!isUrlFresh(track)) {
            try {
                url = await musicService.streamUrl(track.id);
            } catch {
                // fall back to the existing URL
            }
        }
        audio.src = url;
        audio.currentTime = startAt;
        setPosition(startAt);
        setDuration(track.duration_seconds ?? 0);
        if (autoplay) {
            try {
                await audio.play();
            } catch {
                // Autoplay can be blocked until the user interacts with the page.
                setIsPlaying(false);
            }
        }
    }, []);

    const goTo = useCallback((nextIndex: number, autoplay = true) => {
        const { queue: q } = stateRef.current;
        if (nextIndex < 0 || nextIndex >= q.length) {
            audioRef.current?.pause();
            return;
        }
        setIndex(nextIndex);
        void loadAndPlay(q[nextIndex], autoplay);
    }, [loadAndPlay]);

    const next = useCallback(() => {
        const { queue: q, index: i, shuffle: sh, repeat: rp } = stateRef.current;
        if (q.length === 0) return;
        if (sh && q.length > 1) {
            let candidate = i;
            while (candidate === i) candidate = Math.floor(Math.random() * q.length);
            goTo(candidate);
            return;
        }
        if (i + 1 < q.length) {
            goTo(i + 1);
        } else if (rp === 'all') {
            goTo(0);
        } else {
            audioRef.current?.pause();
        }
    }, [goTo]);

    const prev = useCallback(() => {
        const { queue: q, index: i, repeat: rp } = stateRef.current;
        const audio = audioRef.current;
        if (!audio || q.length === 0) return;
        if (audio.currentTime > 3 || (i === 0 && rp !== 'all')) {
            audio.currentTime = 0;
            return;
        }
        goTo(i > 0 ? i - 1 : q.length - 1);
    }, [goTo]);

    // Audio element events.
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        let retriedFor: string | null = null;
        const onTime = () => setPosition(audio.currentTime);
        const onMeta = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => {
            if (stateRef.current.repeat === 'one') {
                audio.currentTime = 0;
                void audio.play();
                return;
            }
            next();
        };
        const onError = () => {
            const track = stateRef.current.current;
            // Signed URL probably expired: fetch a fresh one once.
            if (track && retriedFor !== track.id) {
                retriedFor = track.id;
                void musicService.streamUrl(track.id).then((url) => {
                    audio.src = url;
                    void audio.play().catch(() => setIsPlaying(false));
                }).catch(() => setIsPlaying(false));
            }
        };
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('loadedmetadata', onMeta);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError);
        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('loadedmetadata', onMeta);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
        };
    }, [next]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
        localStorage.setItem(VOLUME_STORAGE_KEY, String(volume));
    }, [volume]);

    useEffect(() => () => {
        audioRef.current?.pause();
    }, []);

    const playQueue = useCallback((tracks: MusicTrack[], startIndex = 0, nextPlaylistId: string | null = null) => {
        if (tracks.length === 0) return;
        const start = Math.min(Math.max(startIndex, 0), tracks.length - 1);
        stateRef.current = { ...stateRef.current, queue: tracks, index: start };
        setQueue(tracks);
        setPlaylistId(nextPlaylistId);
        setIndex(start);
        void loadAndPlay(tracks[start]);
    }, [loadAndPlay]);

    const play = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (!audio.src && stateRef.current.current) {
            void loadAndPlay(stateRef.current.current);
            return;
        }
        void audio.play().catch(() => setIsPlaying(false));
    }, [loadAndPlay]);
    const pause = useCallback(() => audioRef.current?.pause(), []);
    const toggle = useCallback(() => {
        if (audioRef.current && !audioRef.current.paused) {
            pause();
        } else {
            play();
        }
    }, [pause, play]);
    const seek = useCallback((seconds: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, seconds);
            setPosition(audioRef.current.currentTime);
        }
    }, []);
    const setVolume = useCallback((value: number) => setVolumeState(Math.min(1, Math.max(0, value))), []);

    const syncTracks = useCallback((tracks: MusicTrack[]) => {
        const byId = new Map(tracks.map((track) => [track.id, track]));
        setQueue((prevQueue) => {
            if (prevQueue.length === 0) return prevQueue;
            const currentId = stateRef.current.current?.id;
            const nextQueue = prevQueue.filter((track) => byId.has(track.id)).map((track) => byId.get(track.id) as MusicTrack);
            const nextIndex = currentId ? nextQueue.findIndex((track) => track.id === currentId) : -1;
            if (currentId && nextIndex === -1) {
                audioRef.current?.pause();
            }
            setIndex(nextIndex);
            return nextQueue;
        });
    }, []);

    // ---- Report real state to the API (so the UE app / bots can see "now playing").
    const reportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const report = useCallback(() => {
        const { queue: q, current: c } = stateRef.current;
        if (!c) return;
        void musicService.reportState({
            track_id: c.id,
            playlist_id: playlistId,
            queue: q.slice(0, 500).map((track) => track.id),
            position_seconds: audioRef.current?.currentTime ?? 0,
            is_playing: audioRef.current ? !audioRef.current.paused : false,
            volume,
            shuffle,
            repeat,
        }).catch(() => undefined);
    }, [playlistId, repeat, shuffle, volume]);

    useEffect(() => {
        if (!current) return;
        if (reportTimerRef.current) clearTimeout(reportTimerRef.current);
        reportTimerRef.current = setTimeout(report, 600);
        return () => {
            if (reportTimerRef.current) clearTimeout(reportTimerRef.current);
        };
    }, [current, isPlaying, report]);

    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(report, REPORT_POSITION_MS);
        return () => clearInterval(interval);
    }, [isPlaying, report]);

    // ---- Remote control: apply commands sent to the "web" player via the API.
    const lastHandledRevisionRef = useRef<number | null>(null);
    const applyRemote = useCallback(async (state: MusicPlayerState) => {
        const command = state.last_command;
        if (!command) return;
        const value = command.value;
        switch (command.action) {
            case 'pause':
            case 'stop':
                pause();
                if (command.action === 'stop') seek(0);
                break;
            case 'toggle':
                toggle();
                break;
            case 'next':
                next();
                break;
            case 'prev':
                prev();
                break;
            case 'seek':
                seek(Number(value) || 0);
                break;
            case 'volume':
                setVolume(Number(value));
                break;
            case 'shuffle':
                setShuffle(Boolean(value));
                break;
            case 'repeat':
                setRepeat((value === 'one' || value === 'all') ? value : 'off');
                break;
            case 'play':
            case 'load': {
                const wantedId = state.track_id;
                const { queue: q, current: c } = stateRef.current;
                if (command.action === 'play' && (!wantedId || wantedId === c?.id)) {
                    play();
                    break;
                }
                let tracks = q;
                if (command.action === 'load' || !q.some((track) => track.id === wantedId)) {
                    const library = await musicService.tracks();
                    const byId = new Map(library.map((track) => [track.id, track]));
                    const ids = state.queue.length > 0 ? state.queue : library.map((track) => track.id);
                    tracks = ids.map((id) => byId.get(id)).filter((track): track is MusicTrack => Boolean(track));
                }
                const start = Math.max(0, tracks.findIndex((track) => track.id === wantedId));
                if (tracks.length > 0) {
                    playQueue(tracks, start, state.playlist_id);
                    if (!state.is_playing) {
                        setTimeout(() => audioRef.current?.pause(), 0);
                    }
                }
                break;
            }
        }
        setLastRemote({ action: command.action, by: state.updated_by?.name ?? command.by, at: command.at });
    }, [next, pause, play, playQueue, prev, seek, setVolume, toggle]);

    const shouldPoll = remoteListening || queue.length > 0;
    useEffect(() => {
        if (!shouldPoll) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const state = await musicService.player();
                if (cancelled) return;
                const revision = state.last_command?.revision ?? 0;
                if (lastHandledRevisionRef.current === null) {
                    lastHandledRevisionRef.current = revision; // never replay old commands
                    return;
                }
                if (revision > lastHandledRevisionRef.current) {
                    lastHandledRevisionRef.current = revision;
                    await applyRemote(state);
                }
            } catch {
                // offline / unauthorized: try again next tick
            }
        };
        void poll();
        const interval = setInterval(() => void poll(), REMOTE_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [applyRemote, shouldPoll]);

    const value = useMemo<MusicPlayerContextValue>(() => ({
        queue, index, current, playlistId, isPlaying, position, duration, volume, shuffle, repeat, lastRemote,
        playQueue, play, pause, toggle, next, prev, seek, setVolume, setShuffle, setRepeat, setRemoteListening, syncTracks,
    }), [queue, index, current, playlistId, isPlaying, position, duration, volume, shuffle, repeat, lastRemote,
        playQueue, play, pause, toggle, next, prev, seek, setVolume, syncTracks]);

    return <MusicPlayerContext.Provider value={value}>{children}</MusicPlayerContext.Provider>;
};

export const useMusicPlayer = (): MusicPlayerContextValue => {
    const context = useContext(MusicPlayerContext);
    if (!context) {
        throw new Error('useMusicPlayer must be used inside <MusicPlayerProvider>');
    }
    return context;
};
